import { useUserStore } from '@/app/shared/state/UserDataModule';
import { OrganisationInvoiceData2 } from '@/api/klip-api.proxy';
import { escape } from 'html-escaper';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {isEmpty} from 'lodash-es';
import * as yup from 'yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

export const createInvoiceSettingsSchema = () => yup.object({
    email: yup.string().required().max(320).label('E-mail'),
    attention: yup.string().notRequired().max(200).label('Ter attentie van'),
    reference: yup.string().notRequired().max(25).label('Eigen referentie'),
});

export interface IKlInvoiceSettingsData {
    email?: string;
    attention?: string;
    reference?: string;
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlInvoiceSettings',
    emits: ['updateInvoiceData'],
    props: {
        formPath: { type: String, required: true },
        invoiceData: {
            type: Object as () => OrganisationInvoiceData2,
            default: () => ({}),
            required: true
        },
        editMode: { type: Boolean, default: true },
        modHideGroup: { type: Boolean, default: false },
        modAdmin: { type: Boolean, default: false },
        error: { type: Boolean, default: false },
    },
    setup(props, { emit }) {

        const helpdeskMail: string = import.meta.env.VITE_EMAIL_HELPDESK;

        const schema = createInvoiceSettingsSchema();
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema, props.formPath);

        const email = fields.email.getValue<string>();
        const attention = fields.attention.getValue<string>();
        const reference = fields.reference.getValue<string>();

        const isSending = ref<boolean>(false);
        const ownReference= ref<string>('');

        const hasData = computed((): boolean => !isEmpty(props.invoiceData));


        const emailDisplay = computed(() => {
            return props.modAdmin ? '' : email.value;
        });

        const user = computed(() => {
            return useUserStore().user;
        });

        const title = computed(() => {
            return kboNumber.value ? `${escape(name.value)} - ${kboNumber.value}` : escape(name.value);
        });

        const athumiGateWayUnavailable = computed(() => {
            return props.error || _isAthumiGateWayUnavailable.value;
        });

        const name = computed(() => {
            return props.invoiceData && props.invoiceData.company ? props.invoiceData.company :
                user.value.organisation ? user.value.organisation : '';
        });

        const vatNumber = computed(() => {
            return props.invoiceData ? props.invoiceData.vatNumber : '';
        });

        const kboNumber = computed(() => {
            return props.invoiceData ? props.invoiceData.kboNumber : '';
        });

        const _isAthumiGateWayUnavailable = computed(() => {
            return props.invoiceData ? props.invoiceData.isAthumiGatewayServiceUnavailable : false;
        });

        const contactData = computed(() => {
            const streetParts = `${[props.invoiceData.address.street, props.invoiceData.address.streetNumber, props.invoiceData.address.box].join(' ').trim()}`;
            const cityParts = `${[props.invoiceData.address.zip, props.invoiceData.address.city].join(' ').trim()}
                ${props.invoiceData.address.city && props.invoiceData.address.country ?
                ` - ${props.invoiceData.address.country}` : ''}`;
            return `${escape(streetParts)}${streetParts && cityParts ? '<br>' : ''}${escape(cityParts)}`.trim();
        });

        const displayAttention = computed(() => {
            return vatNumber.value ? `BTW ${escape(vatNumber.value)}` : '';
        });

        const _invoiceSettingsData = computed((): IKlInvoiceSettingsData => ({
            email: email.value,
            attention: attention.value,
            reference: reference.value,
        }));

        watch(
            _invoiceSettingsData,
            (newData: IKlInvoiceSettingsData) => {
                emit('updateInvoiceData', newData);
            },
            { immediate: false, deep: true })

        onMounted(() => {
            fields.email.field.resetField({ value: props.invoiceData?.email });
            fields.attention.field.resetField({ value: props.invoiceData?.attention });
            fields.reference.field.resetField({ value: props.invoiceData?.reference });
        })

        return {
            email,
            attention,
            reference,
            helpdeskMail,
            hasData,
            isSending,
            ownReference,
            user,
            title,
            athumiGateWayUnavailable,
            emailDisplay,
            name,
            vatNumber,
            kboNumber,
            contactData,
            displayAttention,
            fields,
        }
    }
})
